import { gql } from '@apollo/client';

/**
 * Broadcast — scio3 producer for the standalone Reporting Engine.
 *
 * scio3 talks Apollo GraphQL to optisigns-open-api, which signs OPTI-HMAC and
 * forwards to os-reporting-engine. The engine owns broadcasts/runs/deliveries;
 * open-api is a stateless proxy that maps the account → tenantRef and the
 * discrete schedule → cron. These operations mirror the engine's shapes
 * (see docs/specs/23-broadcast/spec.md).
 *
 * The documents call the canonical broadcast* schema fields; the former screenReport* names are gone
 * server-side (open-api, 2026-09-03).
 */

// --- Queries ---

/** List the account's scheduled broadcasts (tenant-scoped server-side). */
export const BROADCASTS_QUERY = gql`
  query Broadcasts($assetId: ID, $deviceId: ID) {
    broadcasts(assetId: $assetId, deviceId: $deviceId) {
      id
      name
      enabled
      autoPausedAt
      pauseReason
      lastError
      source {
        type
        deviceId
        screenName
        assetId
        assetName
      }
      sources {
        type
        deviceId
        screenName
        assetId
        assetName
      }
      deliveryMode
      schedule {
        frequency
        cron
        weekDay
        monthDay
        runWindow
        timezone
      }
      channels {
        type
        config
        connectionId
      }
      lastRun {
        id
        status
        scheduledFor
        manual
        createdAt
        error
        artifactUrl
      }
    }
  }
`;

/**
 * One page of the account's broadcasts — same row shape as `broadcasts`, plus server paging
 * truth. open-api clamps `limit` to 1..50 (default 20) and `offset` to >= 0 (default 0) before
 * calling the engine. Gate load-more on `hasMore`, never on items.length vs total.
 */
export const BROADCASTS_PAGE_QUERY = gql`
  query BroadcastsPage($limit: Int, $offset: Int) {
    broadcastsPage(limit: $limit, offset: $offset) {
      items {
        id
        name
        enabled
        autoPausedAt
        pauseReason
        lastError
        source {
          type
          deviceId
          screenName
          assetId
          assetName
        }
        sources {
          type
          deviceId
          screenName
          assetId
          assetName
        }
        deliveryMode
        schedule {
          frequency
          cron
          weekDay
          monthDay
          runWindow
          timezone
        }
        channels {
          type
          config
          connectionId
        }
        lastRun {
          id
          status
          scheduledFor
          manual
          createdAt
          error
          artifactUrl
        }
      }
      total
      hasMore
    }
  }
`;

/** Run history for a single broadcast (polled every 10s by the Captures tab). */
export const BROADCAST_RUNS_QUERY = gql`
  query BroadcastRuns($broadcastId: ID!) {
    broadcastRuns(broadcastId: $broadcastId) {
      id
      status
      scheduledFor
      manual
      createdAt
      error
      artifactUrl
      captureMs
      sourceCount
      capturedCount
      deliveries {
        id
        channelType
        status
        error
        deliveredAt
      }
    }
  }
`;

// --- Mutations ---

export const CREATE_BROADCAST = gql`
  mutation CreateBroadcast($input: CreateBroadcastInput!) {
    createBroadcast(input: $input) {
      id
      name
      enabled
      autoPausedAt
      pauseReason
      lastError
      source {
        type
        deviceId
        screenName
        assetId
        assetName
      }
      sources {
        type
        deviceId
        screenName
        assetId
        assetName
      }
      deliveryMode
      schedule {
        frequency
        cron
        weekDay
        monthDay
        runWindow
        timezone
      }
      channels {
        type
        config
        connectionId
      }
      lastRun {
        id
        status
        scheduledFor
        manual
        createdAt
        error
        artifactUrl
      }
    }
  }
`;

export const UPDATE_BROADCAST = gql`
  mutation UpdateBroadcast($id: ID!, $input: UpdateBroadcastInput!) {
    updateBroadcast(id: $id, input: $input) {
      id
      name
      enabled
      autoPausedAt
      pauseReason
      lastError
      source {
        type
        deviceId
        screenName
        assetId
        assetName
      }
      sources {
        type
        deviceId
        screenName
        assetId
        assetName
      }
      deliveryMode
      schedule {
        frequency
        cron
        weekDay
        monthDay
        runWindow
        timezone
      }
      channels {
        type
        config
        connectionId
      }
      lastRun {
        id
        status
        scheduledFor
        manual
        createdAt
        error
        artifactUrl
      }
    }
  }
`;

export const DELETE_BROADCAST = gql`
  mutation DeleteBroadcast($id: ID!) {
    deleteBroadcast(id: $id)
  }
`;

export const RUN_BROADCAST_NOW = gql`
  mutation RunBroadcastNow($id: ID!) {
    runBroadcastNow(id: $id) {
      jobId
    }
  }
`;

/**
 * "Send test" from the compose dialog: the form as it stands (saved or not) is captured and
 * delivered once, marked "[Test]". Returns the run to poll plus the hourly allowance.
 */
export const TEST_BROADCAST = gql`
  mutation TestBroadcast($input: TestBroadcastInput!) {
    testBroadcast(input: $input) {
      runId
      limit
      remaining
      resetsAt
    }
  }
`;

/** Poll a test send's run — one delivery per channel, plus the snapshot link once captured. */
export const BROADCAST_TEST_RUN_QUERY = gql`
  query BroadcastTestRun($runId: ID!) {
    broadcastTestRun(runId: $runId) {
      id
      status
      error
      artifactUrl
      deliveries {
        id
        channelType
        status
        error
        deliveredAt
      }
    }
  }
`;

/** Retry ONE failed delivery of a run — reuses the stored capture, no new screenshot. */
export const RETRY_BROADCAST_DELIVERY = gql`
  mutation RetryBroadcastDelivery($deliveryId: ID!) {
    retryBroadcastDelivery(deliveryId: $deliveryId)
  }
`;

// --- Captures Library (cross-broadcast files) ---
// Tenant/product is resolved server-side from the company (like every op here) — the client
// only sends filters. `deleteCapture` acts on a run id (the engine has no pin/keep — retention is a bucket lifecycle rule).

export const BROADCAST_CAPTURES_QUERY = gql`
  query BroadcastCaptures($filter: BroadcastCapturesFilterInput) {
    broadcastCaptures(filter: $filter) {
      items {
        runId
        broadcastId
        broadcastName
        capturedAt
        scheduledFor
        manual
        status
        channels
        kind
        bytes
        artifactUrl
        expired
        keep
        isTest
      }
      total
      hasMore
      fileCount
      totalBytes
    }
  }
`;

export const DELETE_CAPTURE = gql`
  mutation DeleteCapture($runId: ID!) {
    deleteCapture(runId: $runId)
  }
`;

// --- Slack workspaces (OptiSigns-owned integration keys) ---
// open-api runs the OAuth itself: it hands the browser an authorize URL, Slack redirects to
// open-api's public callback (/integrations/slack-reports/callback), and the installed
// workspace's bot token is stored as the account's integration key (type 'slack') — also
// manageable under Settings → Integrations → Slack. A broadcast's slack channel binds that key by
// id. Credentials never reach the browser — the list is label/team only, and the channel picker
// runs server-side.

/**
 * What THIS ENVIRONMENT can offer — not what this account has bought (that is
 * `companyInfo.feature.broadcast`). `slack: false` means the environment's Slack app is not
 * usable yet (BROADCAST_SLACK_ENABLED off on open-api), so the Slack delivery option and the
 * Settings → Integrations Slack tab are hidden. The server refuses the same things, so this is
 * only about not offering a dead end.
 *
 * `engine: false` means this environment was never wired to the Reporting Engine at all — no
 * REPORTING_ENGINE_URL or no producer secret — so every broadcast op answers 502 rather than an
 * answer. Nothing should ask here. Without it the move-to-trash dialog's "Could not check whether
 * any broadcast uses this" renders on EVERY asset delete in such an environment, and a notice
 * built for a transient failure becomes permanent furniture (#2148).
 *
 * Deploy order: selecting `engine` against an open-api that lacks it fails GraphQL validation for
 * the WHOLE query, which is why `useBroadcastCapabilities` treats an errored query as "available".
 */
export const BROADCAST_CAPABILITIES_QUERY = gql`
  query BroadcastCapabilities {
    broadcastCapabilities {
      slack
      engine
    }
  }
`;

export const SLACK_OAUTH_INIT = gql`
  mutation SlackOauthInit($label: String, $connectionId: String) {
    slackOauthInit(label: $label, connectionId: $connectionId) {
      authorizeUrl
    }
  }
`;

export const SLACK_CONNECTIONS_QUERY = gql`
  query SlackConnections {
    slackConnections {
      id
      label
      teamName
      createdAt
      updatedAt
    }
  }
`;

export const DELETE_SLACK_CONNECTION = gql`
  mutation DeleteSlackConnection($id: ID!) {
    deleteSlackConnection(id: $id)
  }
`;

export const SLACK_TEST_CONNECTION = gql`
  query SlackTestConnection($connectionId: ID!) {
    slackTestConnection(connectionId: $connectionId) {
      ok
      teamName
      botUser
      error
    }
  }
`;

export const SLACK_CHANNELS_QUERY = gql`
  query SlackChannels($connectionId: ID!) {
    slackChannels(connectionId: $connectionId) {
      ok
      error
      channels {
        id
        name
        isPrivate
      }
    }
  }
`;

// --- Types (mirror engine shapes) ---

/** How a multi-item broadcast is delivered: one message per item, or one rolled-up message. */
export type BroadcastDeliveryMode = 'separate' | 'combined-images' | 'combined-pdf';

export interface BroadcastSource {
  type: string; // 'screen'
  deviceId?: string | null;
  screenName?: string | null;
  /** Asset sources (type 'asset'): captured via the preview player, no screen needed. */
  assetId?: string | null;
  assetName?: string | null;
}

export interface BroadcastSchedule {
  frequency: string; // 'daily' | 'weekdays' | 'weekly' | 'monthly' | 'custom'
  /** Raw 5-field cron when frequency is 'custom'. */
  cron?: string | null;
  weekDay?: number | null;
  monthDay?: number | null;
  runWindow?: number | null;
  timezone: string;
}

export interface BroadcastChannel {
  type: string; // 'teams' | 'webhook' | 'email' | 'pdf' | 'slack'
  config?: Record<string, unknown> | null;
  /** Slack: the stored workspace connection this channel delivers through. */
  connectionId?: string | null;
}

/** A connected Slack workspace — label + team name only, never credentials. */
export interface SlackConnection {
  id: string;
  label?: string | null;
  teamName?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

/** One channel's outcome within a run. */
export interface BroadcastDelivery {
  id: string;
  channelType: string;
  status: string; // 'queued' | 'delivered' | 'failed'
  error?: string | null;
  deliveredAt?: string | null;
}
export interface BroadcastRun {
  id: string;
  captureMs?: number | null;
  /** Sources this run attempted — the broadcast AS IT WAS at run time (not its current items). */
  sourceCount?: number | null;
  /** Sources captured without error in this run. */
  capturedCount?: number | null;
  deliveries?: BroadcastDelivery[] | null;
  /**
   * Engine `RunStatus`, verbatim: 'pending' (queued, no worker yet) | 'capturing' | 'captured' |
   * 'failed'. Delivery outcome is NOT here — a run can be 'captured' while a channel failed.
   */
  status: string;
  /** ISO timestamp — schedule slot, or trigger time for manual runs (server-normalized). */
  scheduledFor?: string | null;
  /** True for on-demand runs (Run now / the create-time test send). */
  manual?: boolean | null;
  createdAt?: string | null;
  error?: string | null;
  artifactUrl?: string | null;
}

export interface Broadcast {
  id: string;
  name: string;
  enabled: boolean;
  /**
   * Set only when the ENGINE paused the broadcast itself (after repeated failed scheduled runs);
   * all null when the user paused it or it is running. Cleared on resume. Read-only.
   */
  autoPausedAt?: string | null;
  /** `auto:failed-runs` today. */
  pauseReason?: string | null;
  /** The run / delivery error that tipped it into the auto-pause. */
  lastError?: string | null;
  /** First item (back-compat). Prefer `sources`. */
  source: BroadcastSource;
  /** Every asset/screen this broadcast captures. */
  sources?: BroadcastSource[] | null;
  deliveryMode?: BroadcastDeliveryMode | string | null;
  schedule: BroadcastSchedule;
  channels: BroadcastChannel[];
  lastRun?: BroadcastRun | null;
}

// --- Operation result / variable shapes ---

export interface BroadcastInput {
  name: string;
  /** Exactly one of `source` / `sources`. */
  source?: BroadcastSource;
  sources?: BroadcastSource[];
  deliveryMode?: BroadcastDeliveryMode;
  schedule: BroadcastSchedule;
  channels: BroadcastChannel[];
  /**
   * Pause/resume. open-api has always accepted it and every query already reads it back — it was
   * simply missing from this input, so the UI could show a broadcast's state but never change it.
   * Omitted on create = enabled.
   */
  enabled?: boolean;
}

export interface BroadcastsResult {
  broadcasts: Broadcast[];
}
export interface BroadcastsVariables {
  assetId?: string;
  deviceId?: string;
}
/**
 * `broadcastsPage` shapes. Named *Result/*Variables — NOT `BroadcastsPage` — because the
 * Settings › Broadcast page component already carries that name.
 */
export interface BroadcastsPageResult {
  broadcastsPage: {
    items: Broadcast[];
    total: number;
    /** Server truth: another page exists. The only correct load-more gate. */
    hasMore: boolean;
  };
}
export interface BroadcastsPageVariables {
  limit?: number;
  offset?: number;
}
export interface RetryBroadcastDeliveryResult {
  retryBroadcastDelivery: boolean;
}
export interface RetryBroadcastDeliveryVariables {
  deliveryId: string;
}

export interface BroadcastRunsResult {
  broadcastRuns: BroadcastRun[];
}

export interface BroadcastRunsVariables {
  broadcastId: string;
}

// --- Captures Library ---
export interface BroadcastCapture {
  runId: string;
  broadcastId: string;
  broadcastName: string;
  capturedAt: string;
  scheduledFor: string;
  manual: boolean;
  status: string;
  channels: string[];
  kind: 'pdf' | 'image';
  bytes: number | null;
  artifactUrl: string | null;
  /** Retention-swept: the file is gone but the row stays as history — no download. */
  expired: boolean;
  keep: boolean;
  /** Captured by "Send test" from the compose dialog, not by a saved broadcast. */
  isTest?: boolean;
}
export interface BroadcastCapturesFilter {
  from?: string;
  to?: string;
  broadcastId?: string;
  channel?: string;
  status?: string;
  search?: string;
  limit?: number;
  offset?: number;
}
export interface BroadcastCapturesResult {
  broadcastCaptures: {
    items: BroadcastCapture[];
    total: number;
    hasMore: boolean;
    fileCount: number;
    totalBytes: number;
  };
}
export interface BroadcastCapturesVariables {
  filter?: BroadcastCapturesFilter;
}
export interface DeleteCaptureResult {
  deleteCapture: boolean;
}
export interface DeleteCaptureVariables {
  runId: string;
}

export interface CreateBroadcastResult {
  createBroadcast: Broadcast;
}

export interface CreateBroadcastVariables {
  input: BroadcastInput;
}

export interface UpdateBroadcastResult {
  updateBroadcast: Broadcast;
}

/**
 * Update is a PATCH server-side — every field of `UpdateBroadcastInput` is optional — so a
 * caller may send just the one thing it is changing (pausing a broadcast, for instance) instead of
 * rebuilding the whole broadcast and risking clobbering a field it never read.
 */
export type BroadcastPatch = Partial<BroadcastInput>;

export interface UpdateBroadcastVariables {
  id: string;
  input: BroadcastPatch;
}

export interface DeleteBroadcastResult {
  deleteBroadcast: boolean;
}

export interface DeleteBroadcastVariables {
  id: string;
}

export interface RunBroadcastNowResult {
  runBroadcastNow: { jobId: string };
}

export interface RunBroadcastNowVariables {
  id: string;
}

/**
 * A "Send test" draft — a broadcast input without its schedule. `broadcastId` = the saved broadcast
 * being edited, if any: the test message links back to it instead of the Broadcast page.
 */
export type TestBroadcastInput = Omit<BroadcastInput, 'schedule' | 'enabled'> & {
  broadcastId?: string;
};

/** The account's "Send test" allowance for the current clock hour. */
export interface BroadcastTestAllowance {
  limit: number;
  remaining: number;
  /** ISO time the allowance resets — the top of the next hour. */
  resetsAt: string;
}

export interface TestBroadcastResult {
  testBroadcast: BroadcastTestAllowance & { runId: string };
}

export interface TestBroadcastVariables {
  input: TestBroadcastInput;
}

export interface BroadcastTestRunResult {
  broadcastTestRun: BroadcastRun;
}

export interface BroadcastTestRunVariables {
  runId: string;
}

export interface SlackOauthInitResult {
  slackOauthInit: { authorizeUrl: string };
}

export interface SlackOauthInitVariables {
  label?: string;
  /** Reconnect an existing connection in place; omit to add a new one. */
  connectionId?: string;
}

export interface SlackConnectionsResult {
  slackConnections: SlackConnection[];
}

export interface DeleteSlackConnectionResult {
  deleteSlackConnection: boolean;
}

export interface DeleteSlackConnectionVariables {
  id: string;
}

/** Live health check of a connected workspace — exercises the token refresh path too. */
export interface SlackTestConnectionResult {
  slackTestConnection: {
    ok: boolean;
    teamName?: string | null;
    botUser?: string | null;
    error?: string | null;
  };
}

export interface SlackTestConnectionVariables {
  connectionId: string;
}

export interface SlackChannelOption {
  id: string;
  name: string;
  isPrivate: boolean;
}

/** `ok: false` = Slack API refused (missing scope, revoked token…) → fall back to manual entry. */
export interface SlackChannelsResult {
  slackChannels: {
    ok: boolean;
    error?: string | null;
    channels: SlackChannelOption[];
  };
}

export interface SlackChannelsVariables {
  connectionId: string;
}

export interface BroadcastCapabilities {
  slack: boolean;
  /** Whether this environment is wired to the Reporting Engine at all. */
  engine: boolean;
}

export interface BroadcastCapabilitiesResult {
  broadcastCapabilities: BroadcastCapabilities;
}
